import { Component, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DownloadFileService } from '../../../../services/download-file/download-file.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { DbService } from '../../../../services/db/db.service';

@Component({
  selector: 'app-view-document',
  templateUrl: './view-document.component.html',
  styleUrl: './view-document.component.css'
})
export class ViewDocumentComponent {
  @Input() uploadForm!: FormGroup;
  @Input() turnoverFiles: any = [];
  @Input() registrationFiles: any = [];
  @Input() ownershipFiles: any = [];
  @Input() viewing: string = '';

  country: any = '';
  membershipFee: number | null = null;
  isExempted: boolean = false;
  membershipData: any = null;

  constructor(
    private downloadService: DownloadFileService, 
    private snackBar: MatSnackBar, 
    private toastr: ToastrService,
    private dialog: MatDialog,
    private router: Router,
    private dbService: DbService
  ) {}

  ngOnInit(): void {
    this.uploadForm?.get('businessIdentificationForm')?.get('country_of_business_registration')?.valueChanges.subscribe((value) => {
      this.filterCountry(value);
    });
    if (!this.country) {
      this.country = sessionStorage.getItem('selectedCountry');
    }
    this.extractLatestPaidStatus();
  }

  extractLatestPaidStatus(): void {
    this.membershipData = this.uploadForm?.value?.status;
    if (this.membershipData && Array.isArray(this.membershipData.status_history)) {
      // Filtering for PAID statuses that have a txn_id and amount property.
      const paidStatuses = this.membershipData.status_history.filter((item: any) => 
        item.status === 'PAID' && item.txn_id && item.amount !== undefined
      );
      if (paidStatuses.length) {
        // Sort descending by datetime to get the latest PAID status.
        paidStatuses.sort((a: any, b: any) => new Date(b.datetime).getTime() - new Date(a.datetime).getTime());
        const latestPaid = paidStatuses[0];
        this.membershipFee = parseFloat(latestPaid.amount);
        // If the txn_id is "exempted" and amount is 0, marking as exempted.
        if (latestPaid.txn_id === 'exempted' && this.membershipFee === 0) {
          this.isExempted = true;
        }
      }
    }
  }


  filterCountry(name: string) {
    this.country = name;
  }


  getFileSize(fileSizeInBytes: any): string { 
    if (fileSizeInBytes < 1024) {
      return fileSizeInBytes + ' B'; // Display size in bytes
    } else if (fileSizeInBytes < 1024 * 1024) {
      return (fileSizeInBytes / 1024).toFixed(2) + ' KB'; // Display size in kilobytes
    } else {
      return (fileSizeInBytes / (1024 * 1024)).toFixed(2) + ' MB'; // Display size in megabytes
    }
  }

  async downLoadFile(id: any) {
    this.snackBar.open('Please wait file is downloading', '', {
      duration: 3000,
    });
    const response = await this.downloadService.onFileDownload(id);
    if (response.status === 'success') {
      this.snackBar.open('File downloaded successfully', 'Close', {
        duration: 6000,
      });
    } else {
      this.toastr.error('Some error occured, could not download the file');
    }
  }

  getRemoveFilesValue(): boolean {
    const currentStatus = this.uploadForm?.value?.status?.current_status;
  
    // Check if current_status is 'CERTIFIED'
    if (currentStatus === 'CERTIFIED') {
      const statusHistory = this.uploadForm?.value?.status?.status_history;
      const certifiedEntry = statusHistory.find((entry: any) => entry.status === 'CERTIFIED' 
      && entry.datetime === this.uploadForm?.value?.status?.status_datetime);
  
      if (certifiedEntry) {
        return !!certifiedEntry.metadata?.remove_files;
      }
    } else if (currentStatus === 'REJECTED') {
      const statusHistory = this.uploadForm?.value?.status?.status_history;
      const rejectedEntry = statusHistory.find((entry: any) => entry.status === 'REJECTED');
  
      if (rejectedEntry) {
        return !!rejectedEntry.remove_files;
      }
    }
  
    // For statuses other than 'CERTIFIED', return false
    return false;
  }

  isStatusSubmitted(): boolean {
    const currentStatus = this.uploadForm?.value?.status?.current_status;
    return currentStatus === 'SUBMITTED';
  }

  async openPaymentModal(): Promise<void> {
    try {
      // Get the current form data
      let formData = this.uploadForm?.getRawValue();
      const appId = sessionStorage.getItem('appId') || formData?.id;
      
      // If we don't have complete form data or it's missing coupon info, load from database
      if (!formData || !(formData as any)?.appliedCoupon) {
        try {
          const freshFormData = await this.dbService.getCertificationForm(appId);
          if (freshFormData) {
            formData = freshFormData;
          }
        } catch (error) {
          console.error('Error loading fresh data:', error);
        }
      }
      
      const businessName = formData?.businessIdentificationForm?.business_name || 'Unknown Business';
      
      // Check if exempted (similar logic as in upload component)
      const exemptedEmb = this.isExempted;

      // Import the PaymentModalComponent dynamically
      const { PaymentModalComponent } = await import('../payment-modal/payment-modal.component');
      
      const dialogRef = this.dialog.open(PaymentModalComponent, {
        width: '800px',
        data: {
          applicationId: appId,
          businessName: businessName,
          exemptedEmb: exemptedEmb,
          formData: formData
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === 'success') {
          // Payment was successful, navigate to dashboard
          this.router.navigate(['/certifications']);
        } else if (result === 'cancelled') {
          console.log('Payment was cancelled');
        }
      });
      
    } catch (error) {
      console.error('Error opening payment modal:', error);
      this.toastr.error('Failed to open payment modal');
    }
  }

}
